/*
  pdp-media-gallery-v2 元素：合并版桌面 PDP 媒体 gallery 的面板交互
  （类型胶囊切换 / 视频播放暂停 / 3D 预热·全屏·Unfolded-Folded 子切换）。

  本元素是 VariantPicker._initSwiperMedias（theme.js）的整块替换锚点：
  root 带 class="swiper-media-images" + data-variant-id，切变体时三面板与胶囊随
  图片区一起被替换 / 缓存还原 —— 所以交互全部放 connectedCallback（替换插入的
  DOM 里内联 <script> 不会执行，custom element 回调每次插入自动重跑），事件统一
  挂 AbortController、disconnectedCallback 一把清。

  内层图片 swiper 复用 <media-gallery-v2>（product-media-gallery-v2.js.liquid，
  不带锚点 class）；theme.js 缓存还原时把 data-last-idx 写在本元素上，而内层读的
  是自身 dataset —— 这里在内层 connectedCallback（插入时按树序在本回调之后）执行
  前转发给它。
*/

class PdpMediaGalleryV2 extends HTMLElement {
  #abort;
  #preloadTimer;
  #modelContainer;
  #modelPlaceholder = null;

  connectedCallback() {
    // 缓存还原路径：转发 data-last-idx，让内层 swiper 停回离开时的 slide
    const inner = this.querySelector('media-gallery-v2');
    if (inner && this.dataset.lastIdx) {
      inner.dataset.lastIdx = this.dataset.lastIdx;
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // 图片轮播由 <media-gallery-v2> 自己初始化，这里不要重复 new Swiper。
    // 视频面板渲染的是 video_tag 的原生 <video>（数据来自变体 metafield，
    // 不再是旧 block 的 <video-media>），直接用原生 play/pause。
    const video = this.querySelector('.product-media-video video');
    const modelContainer = this.querySelector('.product-media-model');
    this.#modelContainer = modelContainer;
    const modelViewers = modelContainer ? modelContainer.querySelectorAll('model-viewer') : [];

    // 统一控制所有模型的缩放（可能有 Unfolded / Folded 两个）
    const setModelZoom = (disabled) => {
      modelViewers.forEach((mv) => {
        if (disabled) {
          mv.setAttribute('disable-zoom', '');
        } else {
          mv.removeAttribute('disable-zoom');
        }
      });
    };

    // 内联默认禁用缩放（避免滚轮/捏合劫持页面滚动）；缩放仅在全屏时开启
    setModelZoom(true);

    // 加载态：每个模型各自转圈，model-viewer 的 load 事件到了就摘掉。
    // 监听前先查一次 loaded —— 执行时模型可能已经就绪（缓存还原路径必然如此），
    // 那时 load 事件早已错过。
    modelViewers.forEach((mv) => {
      const item = mv.closest('.model-viewer-item');
      if (!item) return;
      function markLoaded() {
        // 模型就绪：先让组件正式 dismiss poster —— reveal='auto' 下它并不会自己揭示，
        // poster 还在就等于没揭示，拖拽旋转会被那层拦住。
        // dismiss 自带淡出，那张黑底 poster 会闪一下，所以紧接着把它直接摘掉；
        // shadow root 是 open 的，而这个元素没有暴露 part，CSS ::part() 够不着。
        mv.dismissPoster();
        const shadow = mv.shadowRoot;
        const poster = shadow ? shadow.getElementById('default-poster') : null;
        if (poster) poster.style.display = 'none';
        // 组件自带的进度条原本被 poster 压在下面，poster 一摘就露出来 —— 一条 5px 的
        // 半透明黑条横在浅灰底上。加载期间它本来也被我们的遮罩挡着，直接摘掉。
        const progressBar = shadow ? shadow.getElementById('default-progress-bar') : null;
        if (progressBar) progressBar.style.display = 'none';
        item.classList.add('is-loaded');
      }
      if (mv.loaded) {
        markLoaded();
      } else {
        mv.addEventListener('load', markLoaded, { once: true, signal });
      }
    });

    // 预热：一收到意图信号（指针悬停 / 手指按下 / 键盘聚焦到 3D 按钮）就把模型切成 eager。
    // 实测 loading='eager' 在面板还是 display:none 时照样把模型下完并解码完，
    // 等用户真点进来时通常已经就绪；默认的 lazy 要等面板显示后 IntersectionObserver 回调，
    // 实测从点击到发出 glb 请求还有 ~2.6s 空转，这段是纯浪费。
    const preloadModels = () => {
      modelViewers.forEach((mv) => {
        mv.setAttribute('loading', 'eager');
      });
    };

    const modelTypeBtn = this.querySelector('.media-type-tabs li[data-type="model"]');
    if (modelTypeBtn) {
      ['mouseenter', 'touchstart', 'focus'].forEach((evt) => {
        modelTypeBtn.addEventListener(evt, preloadModels, { once: true, passive: true, signal });
      });
    }

    // 移动端没有 hover，touchstart 只比 click 早一两百毫秒 —— 要它也秒开，就不能等意图信号，
    // 页面 load 完后趁空闲直接预热。代价是每个 PDP 访客都会下模型（从不点 3D 的也一样），
    // 所以省流量模式和 2G/3G 直接跳过，把选择权还给用户。
    // iOS Safari 没有 navigator.connection，判不出网络时按预热处理 —— 否则移动端等于没优化。
    // 必须等 load 之后：LCP 是媒体区那张主图，抢在它前面下模型会直接拖慢首屏。
    const schedulePreload = () => {
      const conn = navigator.connection;
      if (conn && (conn.saveData || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g' || conn.effectiveType === '3g')) return;
      // load 之后再缓一拍，避开紧跟着 load 跑起来的第三方脚本。
      // 这里不用 requestIdleCallback —— 页面在后台标签时它会被推迟到用户切回来，
      // 那样这段就没有确定的执行时机，测不出来也说不清。
      this.#preloadTimer = setTimeout(preloadModels, 1500);
    };

    if (document.readyState === 'complete') {
      schedulePreload();
    } else {
      window.addEventListener('load', schedulePreload, { once: true, signal });
    }

    const enterModelFullscreen = () => {
      if (!modelContainer || modelContainer.classList.contains('is-fullscreen')) return;
      // 移到 <body> 下，逃离被 transform 的祖先（否则 fixed 被困在媒体列内）
      this.#modelPlaceholder = document.createComment('model-fullscreen-placeholder');
      modelContainer.parentNode.insertBefore(this.#modelPlaceholder, modelContainer);
      document.body.appendChild(modelContainer);
      modelContainer.classList.add('is-fullscreen');
      document.body.style.overflow = 'hidden';
      // 全屏开启缩放：滚轮 / 双指捏合放大看细节
      setModelZoom(false);
    };

    const exitModelFullscreen = () => {
      if (!modelContainer || !modelContainer.classList.contains('is-fullscreen')) return;
      modelContainer.classList.remove('is-fullscreen');
      document.body.style.overflow = '';
      // 放回原位
      if (this.#modelPlaceholder && this.#modelPlaceholder.parentNode) {
        this.#modelPlaceholder.parentNode.insertBefore(modelContainer, this.#modelPlaceholder);
        this.#modelPlaceholder.parentNode.removeChild(this.#modelPlaceholder);
        this.#modelPlaceholder = null;
      }
      // 退出全屏：恢复内联禁用缩放，停留在内联 3D 视图（不再切回 image）
      setModelZoom(true);
    };

    const container = this.querySelector('.product-media-container');
    const mobileMq = window.matchMedia('(max-width: 1023px)');

    const setActiveType = (type) => {
      const target = this.querySelector('.product-media-type[data-type="' + type + '"]');
      const current = this.querySelector('.product-media-type.active');
      if (!target || target === current) return;

      // mobile 下 image 态是流式高度（主图 + 缩略图条），video/3D 态会落回容器的
      // aspect-ratio 1/1 导致页面高度跳变 —— 离开 image 前把当时高度冻结成内联值
      // （显式 height 下 aspect-ratio 不再参与），回到 image 时解除恢复流式。
      // 桌面两态都是定高 75vh，不需要也不参与锁高。
      if (mobileMq.matches && container) {
        if (type === 'image') {
          container.style.height = '';
        } else if (current && current.dataset.type === 'image') {
          container.style.height = container.getBoundingClientRect().height + 'px';
        }
      }

      if (current) current.classList.remove('active');
      target.classList.add('active');

      const activeBtn = this.querySelector('.media-type-tabs li.active');
      if (activeBtn) activeBtn.classList.remove('active');
      const btn = this.querySelector('.media-type-tabs li[data-type="' + type + '"]');
      if (btn) btn.classList.add('active');

      if (video) {
        if (type === 'video') {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    };

    // 跨到桌面断点时解除锁高，否则内联 height 会压过桌面的 75vh 规则
    mobileMq.addEventListener('change', (e) => {
      if (!e.matches && container) container.style.height = '';
    }, { signal });

    this.querySelectorAll('.media-type-tabs li').forEach((btn) => {
      btn.addEventListener('click', () => {
        // 直接点进来（没经过 hover / touch，比如键盘或程序化触发）时补一次预热，
        // 省掉 lazy 那段 IntersectionObserver 空转；已经在下的话重复设属性无副作用。
        if (btn.dataset.type === 'model') preloadModels();
        // 点 3D 仅内联展示模型（可拖拽旋转），不再强制全屏
        setActiveType(btn.dataset.type);
      }, { signal });
    });

    // 右上角全屏按钮才进全屏（对齐 DJI）
    const openBtn = modelContainer ? modelContainer.querySelector('.model-fullscreen-open') : null;
    if (openBtn) {
      openBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        enterModelFullscreen();
      }, { signal });
    }

    const closeBtn = modelContainer ? modelContainer.querySelector('.model-fullscreen-close') : null;
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        exitModelFullscreen();
      }, { signal });
    }

    // Unfolded / Folded 子切换：切换显示哪个模型（监听器绑在元素上，全屏移到 body 后仍有效）
    const variantTabs = modelContainer ? modelContainer.querySelectorAll('.model-variant-tab') : [];
    const viewerItems = modelContainer ? modelContainer.querySelectorAll('.model-viewer-item') : [];
    variantTabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const idx = tab.dataset.modelIndex;
        variantTabs.forEach((t) => {
          t.classList.toggle('active', t === tab);
        });
        viewerItems.forEach((item) => {
          item.classList.toggle('active', item.dataset.modelIndex === idx);
        });
      }, { signal });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') exitModelFullscreen();
    }, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#preloadTimer);
    // 切变体整块替换时，若 3D 面板正被移到 <body> 全屏，把它按 placeholder 放回本元素
    // 子树（detached 子树上 insertBefore 依然有效）—— 否则 <body> 上留下孤儿层
    // （Escape 监听已随 abort 失效，关不掉），而 theme.js 缓存的本元素则永久缺 3D 面板。
    if (this.#modelContainer && this.#modelContainer.classList.contains('is-fullscreen')) {
      this.#modelContainer.classList.remove('is-fullscreen');
      document.body.style.overflow = '';
      if (this.#modelPlaceholder && this.#modelPlaceholder.parentNode) {
        this.#modelPlaceholder.parentNode.insertBefore(this.#modelContainer, this.#modelPlaceholder);
        this.#modelPlaceholder.parentNode.removeChild(this.#modelPlaceholder);
      } else {
        this.#modelContainer.remove();
      }
      this.#modelPlaceholder = null;
    }
  }
}

if (!window.customElements.get('pdp-media-gallery-v2')) {
  window.customElements.define('pdp-media-gallery-v2', PdpMediaGalleryV2);
}
